import {useCallback, useEffect, useRef} from 'react';
import {getAllFocusableElements} from './helpers';
import {FocusManagementType} from './types';

/**
 * Implements focus trapping behavior for popovers, and other overlay components.
 * It ensures that keyboard focus remains within a specific container when it's active,
 * improving accessibility for keyboard users. This hook is mostly for internal use
 * in this package, prefer using `useFocusManagement` since it ensures that an opened
 * popover is added to the stack of opened popovers, and prevents adding the keyboard
 * listeners to closed popovers.
 *
 * @param param0
 */
export const useFocusTrap = ({
  isOpen,
  containerRef,
  disable,
  openerElement,
}: FocusManagementType) => {
  const shouldFocusOpenerOnClose = useRef(false);

  useEffect(() => {
    if (isOpen) {
      if (disable) {
        return;
      }

      const container = containerRef?.current;

      if (!container || shouldFocusOpenerOnClose.current) {
        return;
      }

      const focusableElements = getAllFocusableElements(container);

      if (focusableElements.length === 0) {
        return;
      }

      focusableElements[0]?.focus();
      shouldFocusOpenerOnClose.current = true;
    } else {
      if (
        shouldFocusOpenerOnClose.current &&
        openerElement &&
        // prevents auto focusing on the skip to main content anchor tag
        openerElement?.id !== 'skip-to-main-content'
      ) {
        openerElement.focus({preventScroll: true});
      }
    }
  }, [isOpen, disable, containerRef.current]);

  const tabNavigation = useCallback(
    (e: KeyboardEvent) => {
      if (disable) {
        return null;
      }

      shouldFocusOpenerOnClose.current = true;

      const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

      if (!isTabPressed) {
        return null;
      }

      const container = containerRef.current;

      if (!container) {
        return null;
      }

      const focusableElements = getAllFocusableElements(container);

      if (focusableElements.length === 0) {
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement =
        focusableElements[focusableElements.length - 1];

      if (!container?.contains(document.activeElement)) {
        e.preventDefault();
        firstFocusableElement?.focus();
      } else if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          e.preventDefault();
          lastFocusableElement?.focus();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          e.preventDefault();
          firstFocusableElement?.focus();
        }
      }
    },
    [containerRef.current, disable],
  );

  useEffect(() => {
    if (isOpen && !disable) {
      document.addEventListener('keydown', tabNavigation);
    }

    return () => {
      document.removeEventListener('keydown', tabNavigation);
    };
  }, [isOpen, disable, containerRef.current]);
};
